import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaEdit, FaEnvelope, FaPhone, FaBuilding, FaIdBadge, FaChevronLeft } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faHourglass, faCircle, faStar, faCalendarAlt, faTrophy, faChartLine } from '@fortawesome/free-solid-svg-icons';
import serverURL from '../config';
import toast from 'react-hot-toast';
import UserEditModal from '../components/UserEditModal';
import { useAuth } from '../context/AuthContext';

const fetchCandidateHistory = (candidateId) => axios.get(`${serverURL}/users/${candidateId}/history`);

// ── Helpers ───────────────────────────────────────────────────────────────────

const getRolePillStyle = (role) => {
    if (role === 'admin') return 'bg-red-50 text-red-700 border-red-200';
    if (role === 'interviewer') return 'bg-blue-50 text-blue-700 border-blue-200';
    return 'bg-green-50 text-green-700 border-green-200';
};

const getStatusConfig = (status) => {
    if (status === 'completed') return { icon: faCheckCircle, color: 'text-green-600', bg: 'bg-green-50 border-green-200', label: 'Completed', stroke: '#10B981' };
    if (status === 'in_progress') return { icon: faHourglass, color: 'text-amber-500', bg: 'bg-amber-50 border-amber-200', label: 'In Progress', stroke: '#F59E0B' };
    return { icon: faCircle, color: 'text-campus-text-body', bg: 'bg-campus-bg border-campus-border', label: 'Not Started', stroke: '#E5E7EB' };
};

const calcScore = (perf) => {
    if (!perf?.total_mcq_attempted) return null;
    return Math.round((perf.total_mcq_correct / perf.total_mcq_attempted) * 100);
};

const getScoreColor = (pct) => {
    if (pct >= 75) return 'bg-green-500';
    if (pct >= 50) return 'bg-amber-400';
    return 'bg-campus-error';
};

const getScoreTextColor = (pct) => {
    if (pct >= 75) return 'text-green-600';
    if (pct >= 50) return 'text-amber-600';
    return 'text-campus-error';
};

// ── Component ──────────────────────────────────────────────────────────────────

const CandidateDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { role } = useAuth();
    const isAdmin = role === 'admin';
    const [data, setData] = useState({ user: null, history: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        let isMounted = true;
        setMounted(true);
        const loadHistory = async () => {
            setIsLoading(true);
            try {
                const response = await fetchCandidateHistory(id);
                if (isMounted) setData(response.data);
            } catch (error) {
                console.error('Failed to load history:', error);
                if (isMounted) toast.error('Failed to load user details');
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };
        void loadHistory();
        return () => { isMounted = false; };
    }, [id]);

    const handleEditSave = async (updatedData) => {
        setIsSaving(true);
        try {
            await axios.put(`${serverURL}/users/${id}`, updatedData);
            const response = await fetchCandidateHistory(id);
            setData(response.data);
            toast.success('User updated successfully');
            setIsEditModalOpen(false);
        } catch (error) {
            console.error('Failed to update user:', error);
            toast.error('Failed to update user');
        } finally {
            setIsSaving(false);
        }
    };

    // ── Loading state ──────────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="flex flex-col min-h-screen bg-campus-bg items-center justify-center gap-4 animate-fade-slide">
                <div className="relative w-16 h-16">
                    <div className="absolute inset-0 rounded-full border-4 border-campus-border"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-campus-primary border-t-transparent animate-spin"></div>
                </div>
                <p className="text-sm font-semibold text-campus-text-body tracking-widest uppercase animate-pulse">Loading Profile</p>
            </div>
        );
    }

    // ── Not found ──────────────────────────────────────────────────────────────
    if (!data.user) {
        return (
            <div className="flex flex-col min-h-screen bg-campus-bg items-center justify-center gap-4 animate-fade-slide">
                <div className="w-20 h-20 bg-red-50 text-campus-error rounded-full flex items-center justify-center mb-2">
                    <FontAwesomeIcon icon={faCircle} className="text-3xl" />
                </div>
                <h2 className="text-2xl font-bold text-campus-text-primary mt-2">User Not Found</h2>
                <p className="text-campus-text-body px-4 text-center">We couldn't find the requested candidate profile.</p>
                <button
                    onClick={() => navigate('/candidates')}
                    className="mt-6 px-6 py-2.5 bg-campus-primary text-white font-semibold rounded-xl shadow-sm hover:bg-campus-primary-dark hover:-translate-y-0.5 transition-all duration-200"
                >
                    Return to Candidates
                </button>
            </div>
        );
    }

    const { user, history } = data;

    // Derive KPIs
    const completedCount = history.filter(e => e.progress_status === 'completed').length;
    const scores = history.map(e => calcScore(e.performance)).filter(s => s !== null);
    const avgScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
    const initials = user.name ? user.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() : 'U';
    const bestScore = scores.length > 0 ? Math.max(...scores) : null;

    // ── Contact info rows ──────────────────────────────────────────────────────
    const contactInfo = [
        { icon: FaEnvelope, value: user.email || '—' },
        { icon: FaIdBadge, value: user.id_number || '—' },
        { icon: FaPhone, value: user.mobile || '—' },
        { icon: FaBuilding, label: 'Institution', value: user.institution || '—', block: true },
    ];

    return (
        <div className={`flex flex-col min-h-screen bg-[#F3F4F6] transition-opacity duration-500 ease-out ${mounted ? 'opacity-100' : 'opacity-0'}`}>

            {/* Full-width Hero Banner - Animated background */}
            <div className="relative h-[220px] sm:h-[240px] w-full overflow-hidden bg-gray-900 shadow-md flex-shrink-0 animate-fade-slide">
                <div className="absolute inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] bg-repeat" />

                {/* Dynamic gradients */}
                <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-br from-campus-primary/80 via-red-900/90 to-gray-900 z-0" />
                <div className="absolute -top-[100%] -left-[10%] w-[120%] h-[200%] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent animate-[spin_120s_linear_infinite]" />
                <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-gray-900/60 to-transparent z-10" />

                {/* Back button overlap */}
                <button
                    onClick={() => navigate('/candidates')}
                    className="absolute top-6 left-6 z-30 inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white rounded-xl font-medium text-sm transition-all duration-300 hover:-translate-x-1"
                >
                    <FaChevronLeft size={12} />
                    Back
                </button>
            </div>

            <main className="flex-grow w-full px-4 sm:px-6 lg:px-8 -mt-[110px] sm:-mt-[120px] z-20 pb-12">
                <div className="max-w-[1600px] mx-auto w-full">

                    {/* Main Grid: Sidebar (Left) + Content (Right) */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">

                        {/* ── LEFT SIDEBAR: PROFILE & CONTACT (Cols 1-4) ── */}
                        <div className="lg:col-span-4 xl:col-span-3 flex flex-col gap-6 lg:sticky lg:top-6 lg:h-[calc(100vh-3rem)]">

                            {/* Profile Identity Card */}
                            <div
                                className="bg-white rounded-3xl shadow-xl shadow-black/5 overflow-hidden border border-gray-100 flex flex-col items-center pt-6 pb-5 px-5 animate-slide-in relative group"
                                style={{ animationDelay: '100ms', animationFillMode: 'both' }}
                            >
                                <div className="absolute top-0 left-0 w-full h-2 bg-campus-primary" />

                                {/* Avatar */}
                                <div className="relative mb-4 transform transition-transform duration-500 group-hover:scale-105">
                                    {/* Glowing ring */}
                                    <div className="absolute inset-0 rounded-full bg-campus-primary/30 blur-xl scale-110 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 border-white bg-gradient-to-br from-red-50 to-red-100 shadow-xl flex items-center justify-center relative z-10 overflow-hidden">
                                        <span className="text-3xl sm:text-4xl font-heading font-black text-campus-primary/80 tracking-widest drop-shadow-sm">
                                            {initials}
                                        </span>
                                        <div className="absolute inset-0 bg-gradient-to-tr from-black/0 via-white/40 to-white/0 opacity-0 group-hover:opacity-100 transition-transform duration-1000 -translate-x-full group-hover:translate-x-full" />
                                    </div>

                                    <span
                                        className={`absolute bottom-2 right-2 w-5 h-5 rounded-full border-[3px] border-white shadow-md z-20 ${user.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
                                        title={user.is_active ? 'Active' : 'Inactive'}
                                    />
                                </div>

                                <h1 className="text-xl sm:text-2xl font-heading font-extrabold text-gray-900 text-center tracking-tight leading-tight mb-2 break-words w-full">
                                    {user.name}
                                </h1>

                                <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase border ${getRolePillStyle(user.role)}`}>
                                        {user.role}
                                    </span>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase border ${user.is_active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                                        {user.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>

                                {isAdmin && (
                                    <button
                                        onClick={() => setIsEditModalOpen(true)}
                                        className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gray-50 text-gray-700 font-semibold border border-gray-200 hover:bg-campus-primary hover:text-white hover:border-campus-primary hover:shadow-lg hover:shadow-campus-primary/20 transition-all duration-300 transform active:scale-95"
                                    >
                                        <FaEdit size={14} />
                                        Edit Profile
                                    </button>
                                )}
                            </div>

                            {/* Contact Details Card */}
                            <div
                                className="bg-white rounded-3xl shadow-lg shadow-black/5 overflow-hidden border border-gray-100 flex-1 animate-slide-in relative group min-h-0 overflow-y-auto"
                                style={{ animationDelay: '150ms', animationFillMode: 'both' }}
                            >
                                <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50 backdrop-blur-sm sticky top-0 z-10">
                                    <h2 className="text-xs font-bold text-gray-900 uppercase tracking-widest flex items-center gap-2">
                                        <FaIdBadge className="text-campus-primary" />
                                        Contact Information
                                    </h2>
                                </div>
                                <div className="p-2">
                                    {contactInfo.map((info) => (
                                        <div key={info.value} className={`p-4 rounded-2xl hover:bg-red-50/60 transition-colors duration-200 ${info.block ? 'flex flex-col gap-2' : 'flex items-center gap-4'}`}>
                                            {!info.block && (
                                                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white border border-gray-100 shadow-sm flex items-center justify-center text-campus-primary/60 group-hover/card:text-campus-primary transition-colors">
                                                    <info.icon size={15} />
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                                                    {info.block && <info.icon size={10} className="text-campus-primary/60" />}
                                                    {info.label}
                                                </p>
                                                <p className="text-[15px] font-medium text-gray-800 break-words leading-tight">
                                                    {info.value}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* ── RIGHT COLUMN: STATS & HISTORY (Cols 5-12) ── */}
                        <div className="lg:col-span-8 xl:col-span-9 flex flex-col gap-6 lg:gap-8">

                            {/* KPI Banner Row */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 animate-slide-in" style={{ animationDelay: '200ms', animationFillMode: 'both' }}>
                                {/* Event Count */}
                                <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-lg shadow-black/5 flex flex-col justify-between group hover:-translate-y-1 hover:shadow-xl hover:shadow-black/10 hover:border-blue-100 transition-all duration-300">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-inner">
                                            <FontAwesomeIcon icon={faCalendarAlt} className="text-xl" />
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-4xl font-extrabold text-gray-900 tracking-tight">{history.length}</p>
                                        <p className="text-sm font-bold text-gray-500 uppercase tracking-wide mt-1">Total Events</p>
                                    </div>
                                </div>

                                {/* Completed */}
                                <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-lg shadow-black/5 flex flex-col justify-between group hover:-translate-y-1 hover:shadow-xl hover:shadow-black/10 hover:border-green-100 transition-all duration-300">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="w-12 h-12 rounded-2xl bg-green-50 text-green-600 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-inner">
                                            <FontAwesomeIcon icon={faCheckCircle} className="text-xl" />
                                        </div>
                                        {/* Completion ring */}
                                        <svg width="40" height="40" viewBox="0 0 40 40" className="-rotate-90">
                                            <circle cx="20" cy="20" r="16" fill="none" className="stroke-gray-100" strokeWidth="4" />
                                            <circle cx="20" cy="20" r="16" fill="none" className="stroke-green-500 drop-shadow-md" strokeWidth="4" strokeDasharray="100.5" strokeDashoffset={history.length > 0 ? 100.5 - (completedCount / history.length) * 100.5 : 100.5} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s ease-out 0.5s' }} />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-4xl font-extrabold text-gray-900 tracking-tight">{completedCount}</p>
                                        <p className="text-sm font-bold text-gray-500 uppercase tracking-wide mt-1">Completed</p>
                                    </div>
                                </div>

                                {/* Avg Score */}
                                <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-lg shadow-black/5 flex flex-col justify-between group hover:-translate-y-1 hover:shadow-xl hover:shadow-black/10 hover:border-amber-100 transition-all duration-300">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-500 flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300 shadow-inner">
                                            <FontAwesomeIcon icon={faStar} className="text-xl" />
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-4xl font-extrabold text-gray-900 tracking-tight flex items-baseline gap-1">
                                            {avgScore !== null ? avgScore : '—'}
                                            {avgScore !== null && <span className="text-lg font-bold text-gray-400">%</span>}
                                        </p>
                                        <p className="text-sm font-bold text-gray-500 uppercase tracking-wide mt-1">Avg Score</p>
                                    </div>
                                </div>

                                {/* Best Score */}
                                <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-lg shadow-black/5 flex flex-col justify-between group hover:-translate-y-1 hover:shadow-xl hover:shadow-black/10 hover:border-purple-100 transition-all duration-300 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-purple-100 rounded-full blur-3xl -mr-16 -mt-16 opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
                                    <div className="flex justify-between items-start mb-4 relative z-10">
                                        <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-inner">
                                            <FontAwesomeIcon icon={faTrophy} className="text-xl" />
                                        </div>
                                    </div>
                                    <div className="relative z-10">
                                        <p className="text-4xl font-extrabold text-purple-700 tracking-tight flex items-baseline gap-1">
                                            {bestScore !== null ? bestScore : '—'}
                                            {bestScore !== null && <span className="text-lg font-bold text-purple-400">%</span>}
                                        </p>
                                        <p className="text-sm font-bold text-gray-500 uppercase tracking-wide mt-1">Best Score</p>
                                    </div>
                                </div>
                            </div>

                            {/* Engagement History Area */}
                            <div
                                className="bg-white rounded-3xl border border-gray-100 shadow-lg shadow-black/5 overflow-hidden flex-1 flex flex-col animate-slide-in relative"
                                style={{ animationDelay: '250ms', animationFillMode: 'both' }}
                            >
                                {/* Header */}
                                <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white/80 backdrop-blur-md z-10">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400">
                                            <FontAwesomeIcon icon={faChartLine} size="lg" />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-heading font-black text-gray-900 tracking-tight">Test History & Performance</h2>
                                            <p className="text-xs font-semibold text-gray-500 mt-0.5">Timeline of assessments and scoring</p>
                                        </div>
                                    </div>
                                    {history.length > 0 && (
                                        <span className="inline-flex items-center justify-center h-8 px-4 rounded-full bg-gray-900 text-white text-sm font-bold shadow-md">
                                            {history.length} Event{history.length !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="p-8 flex-1 bg-gray-50/30">
                                    {history.length === 0 ? (
                                        <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center max-w-sm mx-auto">
                                            <div className="w-24 h-24 rounded-3xl bg-white shadow-lg border border-gray-100 flex items-center justify-center mb-6 animate-float">
                                                <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-300 text-4xl" />
                                            </div>
                                            <h3 className="text-2xl font-bold text-gray-900 mb-2">No History Found</h3>
                                            <p className="text-gray-500">This candidate has not participated in any events or tests yet. Activity will appear here automatically.</p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6">
                                            {history.map((event, index) => {
                                                const statusCfg = getStatusConfig(event.progress_status);
                                                const scorePct = calcScore(event.performance);
                                                const delay = 300 + (index * 50);

                                                return (
                                                    <div
                                                        key={event.event_id}
                                                        className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col overflow-hidden animate-slide-in relative"
                                                        style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}
                                                    >
                                                        {/* Gradient Top Border */}
                                                        <div className={`h-1.5 w-full flex-shrink-0 transition-colors duration-300 ${scorePct !== null ? getScoreColor(scorePct) : 'bg-gray-200'}`} />

                                                        <div className="p-6 flex flex-col flex-grow gap-5">
                                                            {/* Title */}
                                                            <h3 className="font-heading font-bold text-lg text-gray-900 line-clamp-2 leading-tight group-hover:text-campus-primary transition-colors min-h-[3rem]">
                                                                {event.event_name}
                                                            </h3>

                                                            {/* Metadata Tags */}
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-lg border ${statusCfg.bg}`}>
                                                                    <FontAwesomeIcon icon={statusCfg.icon} className={`text-[11px] ${statusCfg.color}`} />
                                                                    {statusCfg.label}
                                                                </span>
                                                                <span className="text-[11px] font-bold text-gray-500 bg-gray-50 border border-gray-100 px-3 py-1 rounded-lg flex items-center gap-1.5">
                                                                    <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400" />
                                                                    {new Date(event.registration_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                                                </span>
                                                            </div>

                                                            {/* Score Info */}
                                                            <div className="mt-auto pt-5 border-t border-gray-100">
                                                                {scorePct !== null ? (
                                                                    <div className="relative">
                                                                        <div className="flex justify-between items-end mb-2.5">
                                                                            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Test Score</span>
                                                                            <div className="text-right">
                                                                                <span className={`text-2xl font-black tracking-tight leading-none ${getScoreTextColor(scorePct)}`}>
                                                                                    {scorePct}%
                                                                                </span>
                                                                                <p className="text-xs font-semibold text-gray-400 mt-1">
                                                                                    {event.performance.total_mcq_correct} <span className="font-normal text-gray-300">/</span> {event.performance.total_mcq_attempted} correct
                                                                                </p>
                                                                            </div>
                                                                        </div>

                                                                        {/* Thick progress bar */}
                                                                        <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden ring-1 ring-inset ring-black/5">
                                                                            <div
                                                                                className={`h-full rounded-full transition-all duration-1000 ease-out flex justify-end ${getScoreColor(scorePct)}`}
                                                                                style={{ width: `${scorePct}%`, transitionDelay: `${delay + 200}ms` }}
                                                                            >
                                                                                <div className="w-4 h-full bg-white/30 rounded-full mx-1"></div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center justify-center py-4 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                                                        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">No Score Data</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </main>

            {isAdmin && isEditModalOpen && (
                <UserEditModal
                    user={user}
                    onSave={handleEditSave}
                    onCancel={() => setIsEditModalOpen(false)}
                    isSaving={isSaving}
                />
            )}
        </div>
    );
};

export default CandidateDetails;
